/* import __COLOCATED_TEMPLATE__ from './select.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import ENV from 'embercom/config/environment';
import AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import type { EntityType } from 'embercom/models/data/entity-types';
// @ts-ignore
import { createDropdownGroupFromGroupList } from '@intercom/pulse/lib/dropdown-group';
import type IntlService from 'ember-intl/services/intl';
import type { ContentSearchResponse } from 'embercom/lib/content-service/search-api';

interface ContentOption {
  text: string;
  value: string;
  entityType: string;
  isSelected?: boolean;
}

export type EntityTupleWithTitle = {
  entityId: number;
  entityType: EntityType;
  title: string;
};

interface Args {
  isLoadingRecommendedContent: boolean;
  recommendedContent: Array<EntityTupleWithTitle>;
  searcheableEntityTypes: Array<EntityType>;
  onNewClick: () => void;
  onContentClick: (entityId: number, entityType: EntityType) => void;
  isOpen?: boolean;
}

type ContentSelectSignature = {
  Args: Args;
  Element: HTMLDivElement;
  Blocks: {
    default: [];
  };
};

export default class Select extends Component<ContentSelectSignature> {
  @service declare appService: any;
  @service declare intl: IntlService;

  @tracked searchTerm = '';
  @tracked contentApi: AiContentLibraryApi;
  @tracked entitySearchResults: EntityTupleWithTitle[] = [];
  @tracked searchRequested = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.contentApi = new AiContentLibraryApi(
      owner,
      {
        per_page: 10,
        app_id: this.appService.app.id,
        object_types: args.searcheableEntityTypes,
      },
      { persistToStore: false },
    );
  }

  get dropdownGroups() {
    let groups = [];
    if (!this.searchTerm) {
      groups = this.recommendedContentGroups;
    } else {
      groups = this.searchResultGroups;
    }
    return groups;
  }

  get isLoading() {
    return this.args.isLoadingRecommendedContent || this.searchRequested;
  }

  get groupTree() {
    return createDropdownGroupFromGroupList(this.dropdownGroups);
  }

  get recommendedContentGroups() {
    if (isEmpty(this.args.recommendedContent)) {
      return [];
    }
    return [
      {
        heading: this.intl.t('knowledge-hub.content-select.recommended'),
        items: this.toDropdownItems(this.args.recommendedContent),
      },
    ];
  }

  toDropdownItems(entityTuples: EntityTupleWithTitle[]) {
    if (isEmpty(entityTuples)) {
      return [];
    }
    return entityTuples.map((result) => ({
      text: result.title,
      value: `${result.entityType}:${result.entityId}`,
      entityType: result.entityType,
    }));
  }

  get searchResultGroups() {
    if (isEmpty(this.entitySearchResults)) {
      return [];
    }
    return [
      {
        items: this.toDropdownItems(this.entitySearchResults),
      },
    ];
  }

  @action
  onSearchTermChange(value: string) {
    this.searchTerm = value;
    if (isEmpty(value)) {
      this.searchRequested = false;
    } else {
      this.searchRequested = true;
      debounce(this, this.performSearch, ENV.APP._500MS);
    }
  }

  performSearch() {
    taskFor(this.searchTask).perform();
  }

  @restartableTask
  *searchTask() {
    let searchResponse: ContentSearchResponse = yield this.contentApi.search(this.searchTerm);
    this.entitySearchResults =
      searchResponse?.content_wrappers.map(
        (wrapper: { entity_id: number; entity_type: EntityType; title: string }) => ({
          entityId: wrapper.entity_id,
          entityType: wrapper.entity_type,
          title: wrapper.title,
        }),
      ) ?? [];
    this.searchRequested = false;
  }

  @action
  selectItem(item: ContentOption) {
    let [entityType, entityId] = item.value.split(':');
    this.args.onContentClick(Number(entityId), Number(entityType));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentSelect::Select': typeof Select;
    'knowledge-hub/content-select/select': typeof Select;
  }
}
